import onetime from "@-/com.npmjs.onetime";
import pMemoize from "@-/com.npmjs.p-memoize";
import { createAudioPlayer, setAudioModeAsync, type AudioPlayer } from "@-/com.npmjs.expo-audio";
import { Speech } from "@-/com.npmjs.expo-speech";
import { getDeviceSetting, setDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import {
  getNeuralSentenceVoice,
  type NeuralSentenceVoice,
  NORMAL_SENTENCE_PACE,
  type SentencePace,
  SYSTEM_SENTENCE_VOICE_ID,
  type SentenceVoiceId,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voice-catalog";
import {
  canUseNeuralSentenceVoices,
  isNeuralSentenceVoiceInstalled,
  synthesizeSentenceAudio,
} from "@-/dev.improve.haniverse.lib.sentence.speech.voices";
import { requestSentenceVoiceChoice } from "#sentence-voice-choice-request";
import { pickMandarinSystemVoice } from "#pick-mandarin-system-voice";

/**
 * Mandarin read-aloud for example sentences.
 *
 * Sentences have no recorded audio — the bundled mp3 corpus is one clip per *pinyin syllable*
 * (`assets/pinyin/audio`), and stitching those together plays a sentence as a string of isolated
 * citation-tone syllables with no sandhi, prosody, or phrasing. Read-aloud therefore uses either
 * a downloaded neural voice (Kokoro, voiced from the app's own Mandarin reading so every tone is
 * the one the app chose) or the platform's text-to-speech, whichever the learner picked — the
 * first play asks. Single graphemes keep using the recorded clips via `PinyinSoundButton`.
 *
 * Playback is a single global resource, so ownership is tracked here rather than per component:
 * starting a new sentence always interrupts whatever was playing, and every mounted button is told
 * who currently owns playback (and whether that sentence is still being synthesized) so only that
 * one renders as active.
 */

/** Identifies which caller currently owns sentence playback. */
type SpeechOwnerId = string;

export type SentenceSpeechState = {
  ownerId: SpeechOwnerId;
  /** `preparing` while a neural voice synthesizes; `speaking` once audio plays. */
  phase: "preparing" | "speaking";
} | null;

export type SentenceSpeechListener = (state: SentenceSpeechState) => void;

const listeners = new Set<SentenceSpeechListener>();

const playback: {
  /**
   * Bumped whenever audio is started or stopped, so a completion callback from superseded audio
   * (an async synthesis, `onDone`, a player finish event) cannot clear or replace the state of
   * whatever replaced it.
   */
  generation: number;
  /**
   * Bumped by every sentence press and stop, but NOT by voice samples: a sentence waiting for the
   * learner to choose a voice must still play after they listen to samples in the chooser.
   */
  sentenceRequest: number;
  state: SentenceSpeechState;
  /** The sentence waiting for a voice choice, so pressing it again cancels the wait. */
  waitingOwnerId: SpeechOwnerId | null;
} = { generation: 0, sentenceRequest: 0, state: null, waitingOwnerId: null };

/** The system voice's Slow rate; each neural voice has its own slow pace (`slowPace`). */
const SYSTEM_VOICE_SLOW_RATE = 0.8;

function setState(state: SentenceSpeechState): void {
  if (playback.state?.ownerId === state?.ownerId && playback.state?.phase === state?.phase) {
    return;
  }
  playback.state = state;
  for (const listener of listeners) {
    listener(state);
  }
}

/** Subscribe to changes in which caller owns sentence playback. */
export function subscribeToSentenceSpeech(listener: SentenceSpeechListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current playback owner and phase, or `null` when idle. */
export function getSentenceSpeechState(): SentenceSpeechState {
  return playback.state;
}

const configureAudioMode = pMemoize(() =>
  setAudioModeAsync({
    allowsRecording: false,
    interruptionMode: "doNotMix",
    playsInSilentMode: true,
    shouldRouteThroughEarpiece: false,
  }),
);

const neuralPlayerCreated = { value: false };

/** One lazily created player for synthesized sentences (an ExoPlayer per row would exhaust memory). */
const getNeuralPlayer = onetime((): AudioPlayer => {
  const player = createAudioPlayer(null, { keepAudioSessionActive: false });
  player.addListener("playbackStatusUpdate", (status) => {
    if (status.didJustFinish && playback.state?.phase === "speaking") {
      setState(null);
    }
  });
  neuralPlayerCreated.value = true;
  return player;
});

/** Silence everything without touching ownership or the waiting sentence. */
function silence(): void {
  playback.generation += 1;
  // `stop()` rejects on web when nothing is speaking; the state is cleared regardless.
  void Promise.resolve(Speech.stop()).catch(() => {
    // Engine was already idle.
  });
  if (neuralPlayerCreated.value) {
    try {
      getNeuralPlayer().pause();
    } catch {
      // The native player can be torn down under us (fast refresh); nothing to stop then.
    }
  }
}

/** Stop any in-flight sentence (including one waiting for a voice choice) and mark playback idle. */
export function stopSentenceSpeech(): void {
  playback.sentenceRequest += 1;
  playback.waitingOwnerId = null;
  silence();
  setState(null);
}

/**
 * The learner's voice, asking them the first time. `null` when they dismissed the prompt. A
 * caller that must not ask (`canAskForVoice: false`) gets the system voice for this sentence
 * only, leaving the choice open.
 */
async function resolveSentenceVoice(canAskForVoice: boolean): Promise<SentenceVoiceId | null> {
  const chosen = getDeviceSetting("sentenceVoice");
  if (chosen !== null) {
    return chosen;
  }
  // Nothing to choose between (web, or a build without the synthesizer): use the system voice.
  if (!canUseNeuralSentenceVoices()) {
    setDeviceSetting("sentenceVoice", SYSTEM_SENTENCE_VOICE_ID);
    return SYSTEM_SENTENCE_VOICE_ID;
  }
  return canAskForVoice ? requestSentenceVoiceChoice() : SYSTEM_SENTENCE_VOICE_ID;
}

function speakWithSystemVoice(sentence: string, rate: number, ownerId: SpeechOwnerId): void {
  const generation = playback.generation;
  const finish = () => {
    if (generation === playback.generation) {
      setState(null);
    }
  };
  setState({ ownerId, phase: "speaking" });
  void pickMandarinSystemVoice().then((voice) => {
    if (generation !== playback.generation) {
      return;
    }
    Speech.speak(sentence, {
      language: "zh-CN",
      onDone: finish,
      onError: finish,
      onStopped: finish,
      rate,
      ...(voice === undefined ? {} : { voice }),
    });
  });
}

async function playOnNeuralPlayer(
  source: number | string,
  ownerId: SpeechOwnerId,
  generation: number,
): Promise<void> {
  await configureAudioMode();
  if (generation !== playback.generation) {
    return;
  }
  const player = getNeuralPlayer();
  player.replace(source);
  player.play();
  setState({ ownerId, phase: "speaking" });
}

async function speakWithNeuralVoice({
  getIsMuted,
  isSlow,
  ownerId,
  sentence,
  voice,
}: {
  getIsMuted: () => boolean;
  isSlow: boolean;
  ownerId: SpeechOwnerId;
  sentence: string;
  voice: NeuralSentenceVoice;
}): Promise<void> {
  const generation = playback.generation;
  setState({ ownerId, phase: "preparing" });
  let uri: string;
  try {
    const pace: SentencePace = isSlow ? voice.slowPace : NORMAL_SENTENCE_PACE;
    uri = await synthesizeSentenceAudio({ pace, sentence, voice });
  } catch (error) {
    // A broken download or model must not leave the button silent: say it with the system voice.
    logger(import.meta.location).warn(
      "Neural sentence voice failed; using the system voice",
      error,
    );
    if (generation === playback.generation && !getIsMuted()) {
      speakWithSystemVoice(sentence, isSlow ? SYSTEM_VOICE_SLOW_RATE : 1, ownerId);
    }
    return;
  }
  if (generation !== playback.generation) {
    return;
  }
  if (getIsMuted()) {
    setState(null);
    return;
  }
  await playOnNeuralPlayer(uri, ownerId, generation);
}

function speakWithVoice(options: {
  getIsMuted: () => boolean;
  ownerId: SpeechOwnerId;
  sentence: string;
  voiceId: SentenceVoiceId;
}): Promise<void> {
  const { getIsMuted, ownerId, sentence, voiceId } = options;
  const isSlow = getDeviceSetting("sentenceSpeechRate") === "slow";
  const voice = getNeuralSentenceVoice(voiceId);
  if (voice !== undefined && isNeuralSentenceVoiceInstalled(voice)) {
    return speakWithNeuralVoice({ getIsMuted, isSlow, ownerId, sentence, voice });
  }
  // The system voice, or a neural voice whose files were removed since it was chosen.
  speakWithSystemVoice(sentence, isSlow ? SYSTEM_VOICE_SLOW_RATE : 1, ownerId);
  return Promise.resolve();
}

/**
 * Read `sentence` aloud in Mandarin, interrupting whatever was playing.
 *
 * Pressing the button of the sentence that is already playing (or synthesizing, or waiting for a
 * voice choice) stops it — the icon doubles as a stop control, which is why `ownerId` is compared
 * before starting rather than unconditionally restarting. `mode: "start"` leaves a sentence that
 * is already playing alone instead.
 *
 * Without a chosen voice, the first sentence asks for one (`registerSentenceVoiceChooser`).
 * `canAskForVoice: false` is for a surface the chooser sheet cannot appear over (a full-screen
 * quest call): it speaks with the system voice until the learner chooses.
 */
export function speakSentence({
  canAskForVoice = true,
  getIsMuted,
  mode = "toggle",
  ownerId,
  sentence,
}: {
  canAskForVoice?: boolean;
  getIsMuted: () => boolean;
  mode?: "start" | "toggle";
  ownerId: SpeechOwnerId;
  sentence: string;
}): void {
  if (playback.state?.ownerId === ownerId || playback.waitingOwnerId === ownerId) {
    if (mode === "toggle") {
      stopSentenceSpeech();
    }
    return;
  }
  // Bumping both counters is what makes the previous sentence's late callbacks no-ops: the engine
  // fires `onDone` when `stop()` cuts it off, and without the guard that callback would clear the
  // owner we are about to set.
  playback.sentenceRequest += 1;
  const request = playback.sentenceRequest;
  silence();
  setState(null);
  if (getIsMuted() || sentence.trim().length === 0) {
    return;
  }
  void (async () => {
    playback.waitingOwnerId = ownerId;
    const voiceId = await resolveSentenceVoice(canAskForVoice);
    if (playback.waitingOwnerId === ownerId) {
      playback.waitingOwnerId = null;
    }
    if (voiceId === null || request !== playback.sentenceRequest || getIsMuted()) {
      return;
    }
    // Interrupt a voice sample the learner was listening to while choosing.
    silence();
    await speakWithVoice({ getIsMuted, ownerId, sentence, voiceId });
  })().catch((error: unknown) => {
    logger(import.meta.location).warn("Sentence read-aloud failed", error);
    if (request === playback.sentenceRequest) {
      setState(null);
    }
  });
}

function toggleSample(ownerId: SpeechOwnerId): boolean {
  if (playback.state?.ownerId === ownerId) {
    silence();
    setState(null);
    return false;
  }
  silence();
  return true;
}

/**
 * Play a recorded sample of a voice (bundled with the app, so a voice can be heard before it is
 * downloaded). Samples interrupt sentences and each other, but never cancel a sentence that is
 * waiting for the learner to pick a voice.
 */
export function playSentenceVoiceSample({
  ownerId,
  source,
}: {
  ownerId: SpeechOwnerId;
  source: number;
}): void {
  if (toggleSample(ownerId)) {
    void playOnNeuralPlayer(source, ownerId, playback.generation);
  }
}

/** Preview an installed (or the system) voice live with a sample sentence. */
export function previewSentenceVoice({
  ownerId,
  sentence,
  voiceId,
}: {
  ownerId: SpeechOwnerId;
  sentence: string;
  voiceId: SentenceVoiceId;
}): void {
  if (toggleSample(ownerId)) {
    void speakWithVoice({ getIsMuted: () => false, ownerId, sentence, voiceId });
  }
}
